import React, { useEffect } from 'react';
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes, FaQuestionCircle } from 'react-icons/fa';

const CustomModal = ({ 
    isOpen, 
    type, 
    title, 
    message, 
    onConfirm, 
    onCancel, 
    confirmText, 
    cancelText, 
    onClose 
}) => {
    // Prevent scrolling when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => { document.body.style.overflow = 'unset'; };
    }, [isOpen]);

    if (!isOpen) return null;

    const getIcon = () => {
        switch (type) {
            case 'success': return <FaCheckCircle className="text-green-500" size={40} />;
            case 'error': return <FaExclamationCircle className="text-[#c59b27]" size={40} />;
            case 'confirm': return <FaQuestionCircle className="text-[#111]" size={40} />;
            case 'info': 
            default: return <FaInfoCircle className="text-[#c59b27]" size={40} />;
        }
    };

    const isConfirm = type === 'confirm';

    return (
        <div className="fixed inset-0 z-[1000] flex items-center justify-center p-6 lg:p-12">
            {/* Backdrop */}
            <div 
                className="absolute inset-0 bg-[#111]/40 backdrop-blur-md transition-opacity duration-500 animate-fade-in"
                onClick={onClose}
            />

            {/* Modal Content */}
            <div className="relative bg-white w-full max-w-md shadow-2xl overflow-hidden animate-fade-up border border-[#f0f0f0]">
                {/* Accent Top Border */}
                <div className={`h-1 w-full ${type === 'success' ? 'bg-green-500' : 'bg-[#c59b27]'}`} />
                
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-[#ccc] hover:text-[#111] transition-colors"
                >
                    <FaTimes size={16} />
                </button>

                <div className="p-8 lg:p-10 text-center space-y-6">
                    <div className="flex justify-center">
                        {getIcon()}
                    </div>

                    <div className="space-y-2">
                        {title && (
                            <h3 className="text-xl font-black uppercase tracking-tight text-[#111]">
                                {title}
                            </h3>
                        )}
                        <p className="text-[#555] text-sm font-medium leading-relaxed">
                            {message}
                        </p>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                        {isConfirm && (
                            <button
                                onClick={onCancel}
                                className="flex-1 py-4 px-6 bg-[#f5f5f5] text-[#111] text-[11px] font-bold uppercase tracking-widest hover:bg-[#e8e8e8] transition-all duration-300"
                            >
                                {cancelText}
                            </button>
                        )}
                        <button
                            onClick={onConfirm}
                            className={`flex-1 py-4 px-6 text-white text-[11px] font-bold uppercase tracking-widest transition-all duration-300 shadow-lg ${
                                type === 'success' ? 'bg-green-600 hover:bg-green-700 shadow-green-500/20' : 'bg-[#c59b27] hover:bg-[#b0871e] shadow-amber-500/20'
                            }`}
                        >
                            {confirmText}
                        </button>
                    </div>
                </div>
            </div>

            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; }
                    to { opacity: 1; }
                }
                @keyframes fadeUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fade-in {
                    animation: fadeIn 0.4s ease-out forwards;
                }
                .animate-fade-up {
                    animation: fadeUp 0.5s cubic-bezier(0.16, 1, 0.3, 1) forwards;
                }
            `}</style>
        </div>
    );
};

export default CustomModal;
